import React, { useState, useEffect } from 'react';
import { MdCalculate, MdInfoOutline, MdAccountBalanceWallet, MdMonetizationOn } from 'react-icons/md';

export default function LoanCalculator() {
  const [loanType, setLoanType] = useState('maendeleo'); // 'maendeleo' au 'dharura'
  const [memberSavings, setMemberSavings] = useState(1500000); // Kiwango cha akiba
  const [loanAmount, setLoanAmount] = useState(1000000);
  const [loanTerm, setLoanTerm] = useState(12); // Miezi
  
  // Matokeo ya kikokotoo
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalRepayment, setTotalRepayment] = useState(0);
  const [maxAllowableLoan, setMaxAllowableLoan] = useState(0);
  const [errorWarning, setErrorWarning] = useState('');

  // Kikomo cha mkopo kulingana na aina
  useEffect(() => {
    if (loanType === 'maendeleo') {
      const max3x = memberSavings * 3;
      setMaxAllowableLoan(max3x);
      if (loanAmount > max3x) {
        setErrorWarning(`Kiwango kimezidi mara 3 ya akiba yako (Kiwango cha juu unachoweza kukopa ni Tsh ${max3x.toLocaleString()})`);
      } else if (loanAmount > 10000000) {
        setErrorWarning('Mkopo wa maendeleo hautakiwi kuzidi Tsh 10,000,000');
      } else {
        setErrorWarning('');
      }
    } else {
      setMaxAllowableLoan(1000000); // Mkopo wa dharura mwisho milioni moja
      if (loanAmount > 1000000) {
        setErrorWarning('Mkopo wa dharura hautakiwi kuzidi Tsh 1,000,000');
      } else if (loanTerm > 6) {
        setErrorWarning('Marejesho ya mkopo wa dharura hayapaswi kuzidi miezi 6');
      } else {
        setErrorWarning('');
      }
    }
  }, [loanType, loanAmount, loanTerm, memberSavings]);

  // Hesabu za kifedha (Financial Calculations)
  useEffect(() => {
    if (loanType === 'dharura') {
      // Masharti maalum ya dharura: Riba ni laki mbili, malipo ya mkupuo mwisho wa miezi 6
      setTotalInterest(200000);
      setTotalRepayment(loanAmount + 200000);
      setMonthlyPayment((loanAmount + 200000) / loanTerm);
    } else {
      // Mkopo wa Maendeleo: Reducing Balance Method (14% kwa mwaka = 1.1667% kwa mwezi)
      const annualRate = 0.14;
      const monthlyRate = annualRate / 12;
      
      if (loanAmount > 0 && loanTerm > 0) {
        // Mfumo wa Amortization wa kiwango cha juu (Reducing balance formula)
        const x = Math.pow(1 + monthlyRate, loanTerm);
        const monthly = (loanAmount * monthlyRate * x) / (x - 1);
        
        const totalPayable = monthly * loanTerm;
        const totalInt = totalPayable - loanAmount;

        setMonthlyPayment(monthly);
        setTotalInterest(totalInt);
        setTotalRepayment(totalPayable);
      }
    }
  }, [loanType, loanAmount, loanTerm]);

  return (
    <section className="LoanCalculatorSection py-5 position-relative overflow-hidden">
      <div className="abstract-circle circle-top-right"></div>
      <div className="abstract-circle circle-bottom-left"></div>

      <div className="container py-4 position-relative z-index-2">
        <div className="text-center mb-5">
          <span className="calculator-badge mb-2 d-inline-block text-uppercase fw-bold">Zana ya Kifedha</span>
          <h2 className="calculator-main-title fw-bold text-dark">Kikokotoo cha Mikopo ya <span className="text-gradient-primary">YCSC</span></h2>
          <p className="text-muted mx-auto style-subtitle">Kadiria marejesho na riba ya mkopo wako kwa uwazi kabisa kulingana na Sera za Ushirika.</p>
        </div>

        <div className="row g-4 justify-content-center">
          {/* UPANDE WA KUSHOTO: INPUTS FORMS */}
          <div className="col-12 col-lg-6">
            <div className="calculator-card-input p-4 p-md-5 h-100">
              
              {/* Tab Selector ya Aina ya Mkopo */}
              <div className="loan-type-tabs d-flex p-1.5 mb-4 rounded-pill bg-light">
                <button 
                  className={`btn flex-fill rounded-pill py-2.5 fw-bold transition-all ${loanType === 'maendeleo' ? 'bg-primary text-white shadow-sm' : 'text-muted'}`}
                  onClick={() => { setLoanType('maendeleo'); setLoanAmount(1000000); setLoanTerm(12); }}
                >
                  Mkopo wa Maendeleo
                </button>
                <button 
                  className={`btn flex-fill rounded-pill py-2.5 fw-bold transition-all ${loanType === 'dharura' ? 'bg-primary text-white shadow-sm' : 'text-muted'}`}
                  onClick={() => { setLoanType('dharura'); setLoanAmount(1000000); setLoanTerm(6); }}
                >
                  Mkopo wa Dharura
                </button>
              </div>

              {/* Input 1: Kiwango cha Akiba (Inaonekana tu kwenye Maendeleo ili kupima x3) */}
              {loanType === 'maendeleo' && (
                <div className="form-group-custom mb-4">
                  <label className="form-label d-flex align-items-center gap-2 text-dark fw-semibold mb-2">
                    <MdAccountBalanceWallet className="text-primary" size={18} />
                    <span>Kiwango cha Akiba Zako Hivi Sasa (Tsh)</span>
                  </label>
                  <input 
                    type="number" 
                    className="form-control-custom w-100" 
                    value={memberSavings}
                    onChange={(e) => setMemberSavings(Number(e.target.value))}
                  />
                  <small className="text-muted d-block mt-1">Kigezo: Unaruhusiwa kukopa hadi mara 3 ya akiba hii (Max: Tsh {maxAllowableLoan.toLocaleString()}).</small>
                </div>
              )}

              {/* Input 2: Kiasi cha Kukopa */}
              <div className="form-group-custom mb-4">
                <label className="form-label d-flex align-items-center gap-2 text-dark fw-semibold mb-2">
                  <MdMonetizationOn className="text-primary" size={18} />
                  <span>Kiasi Unachoomba Kukopa (Tsh)</span>
                </label>
                <input 
                  type="number" 
                  className="form-control-custom w-100" 
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                />
              </div>

              {/* Input 3: Muda wa Marejesho (Miezi) */}
              <div className="form-group-custom mb-4">
                <label className="form-label d-flex align-items-center gap-2 text-dark fw-semibold mb-2">
                  <MdCalculate className="text-primary" size={18} />
                  <span>Muda wa Marejesho ({loanTerm} Miezi)</span>
                </label>
                <input 
                  type="range" 
                  className="form-range-custom w-100" 
                  min="1" 
                  max={loanType === 'dharura' ? "6" : "36"} 
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(Number(e.target.value))}
                />
                <div className="d-flex justify-content-between small text-muted mt-1">
                  <span>Mwezi 1</span>
                  <span>Upeo: {loanType === 'dharura' ? "Miezi 6" : "Miezi 36"}</span>
                </div>
              </div>

              {/* Onyo la Makosa kama yapo */}
              {errorWarning && (
                <div className="alert-custom-warning p-3 rounded d-flex align-items-start gap-2 text-danger">
                  <MdInfoOutline className="flex-shrink-0 mt-0.5" size={20} />
                  <span className="small fw-medium">{errorWarning}</span>
                </div>
              )}

            </div>
          </div>

          {/* UPANDE WA KULIA: DISPLAY RESULTS (PREMIUM PREMIUM LOOK) */}
          <div className="col-12 col-lg-5">
            <div className="calculator-card-results p-4 p-md-5 h-100 text-white d-flex flex-column justify-content-between position-relative overflow-hidden">
              <div className="results-watermark-bg">YCSC</div>
              
              <div>
                <h4 className="fw-bold mb-4 text-white-90 border-bottom border-white border-opacity-10 pb-3">Mchanganuo wa Makadirio</h4>
                
                {/* Malipo ya Kila Mwezi */}
                <div className="mb-4">
                  <span className="text-white-50 small d-block text-uppercase letter-spacing-1">Malipo ya Kila Mwezi</span>
                  <h2 className="fw-bold display-6 mt-1 text-warning">
                    Tsh {Math.round(monthlyPayment).toLocaleString()}
                  </h2>
                </div>

                <div className="row g-3 pt-2">
                  {/* Jumla ya Riba */}
                  <div className="col-6">
                    <div className="result-sub-box p-3 rounded bg-white bg-opacity-5">
                      <span className="text-primary d-block small">Jumla ya Riba</span>
                      <span className="fw-bold d-block fs-5 mt-1 text-info text-truncate">
                        Tsh {Math.round(totalInterest).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  {/* Mfumo wa Riba */}
                  <div className="col-6">
                    <div className="result-sub-box p-3 rounded bg-white bg-opacity-5">
                      <span className="text-primary d-block small">Njia ya Riba</span>
                      <span className="fw-bold d-block fs-6 mt-1 text-info text-truncate">
                        {loanType === 'dharura' ? 'Kiwango Maalum' : '14% Ulalo (Reducing)'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Jumla Kuu ya Marejesho */}
              <div className="mt-5 pt-3 border-top border-white border-opacity-10">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <span className="text-white-50 small d-block">Jumla ya Kurejesha (Kuu + Riba)</span>
                    <h3 className="fw-bold m-0 fs-3 text-white mt-1">
                      Tsh {Math.round(totalRepRepayment(totalRepayment)).toLocaleString()}
                    </h3>
                  </div>
                </div>

                {loanType === 'dharura' && (
                  <div className="p-2.5 rounded bg-warning bg-opacity-10 border border-warning border-opacity-20 mb-3">
                    <p className="m-0 small text-warning-light">
                      * Mkopo wa dharura unalipwa kwa mkupuo mmoja (Single bullet payment) ndani ya miezi 6. Pia unaweza kulipa kila mwezi hadi ukamaliza
                    </p>
                  </div>
                )}

                {loanType === 'maendeleo' && (
                  <div className="p-2.5 rounded bg-warning bg-opacity-10 border border-warning border-opacity-20 mb-3">
                    <p className="m-0 small text-warning-light">
                      * Kiwango cha mwisho cha mkopo wa maendeleo ni TZS 10, 000, 000
                    </p>
                  </div>
                )}

                <button 
                  className="btn btn-premium-apply w-100 py-3 fw-bold text-uppercase d-flex align-items-center justify-content-center gap-2"
                  disabled={!!errorWarning}
                  onClick={() => window.open("https://ycs.amala.app/#/login", "_blank")}
                >
                  <span>Omba Mkopo Huu Sasa</span>
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );

  // Kurekebisha jina lisilo sahihi la variable kwenye return
  function totalRepRepayment(val) {
    return val;
  }
}